'use client';

import {
  alpha,
  Box,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { SyntheticEvent, useEffect, useMemo, useState } from 'react';

import { ClientOnly } from '@/components/ClientOnlyMarquee';
import GlobalBlockRenderer from '@/components/ContentBlocks/GlobalBlockRenderer/GlobalBlockRenderer';
import { TwoColumnTextImageContent } from '@/components/ContentBlocks/TwoColumnTextImageBlock/TwoColumnTextImageContent';
import { CustomBackground } from '@/components/CustomBackground/CustomBackground';
import { StrapiBackgroundOptions, StrapiDisplayOptions } from '@/lib/DisplayOptionsHelper';
import {
  ComponentBlocksProductFeatures,
  ComponentBlocksTwoColumnTextImageBlock,
  Enum_Componentblocksproductfeatures_Componentbackgroundcolour as Componentbackgroundcolour,
  Enum_Componentshareddisplayoptions_Contentswidth,
} from '@/types';

interface ProductFeaturesProps {
  props: ComponentBlocksProductFeatures;
  spacingTop?: number;
  spacingBottom?: number;
}

export function ProductFeatures({ props }: ProductFeaturesProps) {
  const theme = useTheme();

  const [value, setValue] = useState(0);
  const [tabs, setTabs] = useState(props.Tabs || []);

  useEffect(() => {
    setTabs(props.Tabs || []);
  }, [props.Tabs]);

  const handleChange = (_event: SyntheticEvent | null, newValue: number) => setValue(newValue);

  // Mobile hamburger state
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleSelect = (index: number) => {
    setValue(index);
    handleClose();
  };
  const innerBackground = useMemo(() => {
    if (props.ComponentBackgroundColour === Componentbackgroundcolour.Primary) {
      return `   radial-gradient(at 30% 50%, ${alpha(
        theme.palette.brand.brandDark,
        0.8
      )}, transparent 60%), ${theme.palette.purple[800]}`;
    }
    if (props.ComponentBackgroundColour === Componentbackgroundcolour.PurpleLight) {
      return theme.palette.purple[200];
    }
    if (props.ComponentBackgroundColour === Componentbackgroundcolour.Purple) {
      return theme.palette.purple[400];
    }
    return theme.palette.common.white;
  }, [props?.ComponentBackgroundColour, theme.palette]);

  return (
    <CustomBackground
      backgroundOptions={(props?.ProductFeaturesBackground ?? undefined) as StrapiBackgroundOptions}
      displayOptions={
        {
          ...props?.ProductFeaturesDisplayOptions,
          ContentsWidth:
            props?.ProductFeaturesDisplayOptions?.ContentsWidth ??
            Enum_Componentshareddisplayoptions_Contentswidth.Wide,
          PaddingTop: props?.ProductFeaturesDisplayOptions?.PaddingTop ?? 'md',
          PaddingBottom: props?.ProductFeaturesDisplayOptions?.PaddingBottom ?? 'md',
          PaddingInline: props?.ProductFeaturesDisplayOptions?.PaddingInline ?? 'xs',
        } as StrapiDisplayOptions
      }
    >
      <Box data-component="ProductFeatures">
        {/* Header */}
        <Box
          sx={{
            mb: 6,
            maxWidth: 'sm',
            mx: 'auto',
            justifyItems: 'center',
            textAlign: 'center',
            '> *': {
              textAlign: 'center',
            },
          }}
        >
          <GlobalBlockRenderer content={props.HeaderText || []} />
        </Box>

        {/* Tabs / Hamburger + Tab Panels – mount after layout so MUI Tabs and state work on first load */}
        <ClientOnly
          fallback={
            tabs.length > 0 && (
              <Box
                sx={{
                  color:
                    props?.ComponentBackgroundColour === Componentbackgroundcolour.Primary
                      ? theme.palette.common.white
                      : 'inherit',
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: { xs: 3, md: 6 },
                  py: { xs: 4, sm: 5 },
                  px: { xs: 3, md: 5 },
                  background: innerBackground,
                  borderRadius: '2rem',
                  mx: 'auto',
                  maxWidth: '1200px',
                }}
              >
                <TwoColumnTextImageContent
                  block={
                    {
                      Image: tabs[0]?.Image,
                      ImageAlignment: props?.ProductFeaturesImageAlignment,
                      ImageColRight: tabs[0]?.ImageColRight,
                      ImageColumnSize: props.ProductFeaturesImageColumnSize,
                      TwoColumnBody: tabs[0]?.Body,
                      TextColumnSize: props.ProductFeaturesTextColumnSize,
                      ImageBorder: props.ProductFeaturesImageBorder,
                      RoundImageCorners: props.ProductFeaturesRoundImageCorners,
                      ItemSpacing: props.ProductFeaturesItemSpacing,
                    } as ComponentBlocksTwoColumnTextImageBlock
                  }
                />
              </Box>
            )
          }
        >
          {/* Tabs / Hamburger */}
          {tabs.length > 1 && (
            <Box sx={{ mb: { xs: 3, sm: 5 }, textAlign: 'center' }}>
              {isMobile ? (
                <>
                  {/* Hamburger pill button */}
                  <Box
                    onClick={handleClick}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 3,
                      py: 1.5,
                      backgroundColor: theme.palette.purple[700],
                      borderRadius: '2rem',
                      color: 'white',
                      cursor: 'pointer',
                      minWidth: '8rem',
                      fontWeight: 500,
                      '&:hover': { backgroundColor: theme.palette.purple[600] },
                    }}
                  >
                    <Typography noWrap>{tabs[value]?.Title}</Typography>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </Box>

                  {/* White dropdown */}
                  <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                    PaperProps={{
                      sx: {
                        backgroundColor: 'common.white',
                        mt: 1,
                        minWidth: 150,
                        borderRadius: '1rem',
                      },
                    }}
                  >
                    {tabs.map((tab, i) => (
                      <MenuItem
                        key={i}
                        selected={i === value}
                        onClick={() => handleSelect(i)}
                        sx={{
                          color: 'text.primary',
                          '&.Mui-selected': {
                            backgroundColor: theme.palette.purple[100],
                            fontWeight: 600,
                          },
                          '&:hover': {
                            backgroundColor: theme.palette.purple[50],
                          },
                        }}
                      >
                        {tab?.Title}
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              ) : (
                // Desktop tabs
                <Box
                  sx={{
                    display: 'inline-flex',
                    backgroundColor: theme.palette.purple[700],
                    borderRadius: '2rem',
                    px: 2,
                    py: 1,
                    mx: 'auto',
                  }}
                >
                  <Tabs
                    value={value}
                    onChange={handleChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                      '.MuiTabs-flexContainer': {
                        gap: 3,
                        justifyContent: 'center',
                      },
                      button: {
                        textTransform: 'none',
                        px: 4,
                        py: 1.5,
                        borderRadius: '2rem',
                        backgroundColor: 'transparent',
                        color: 'white',
                        fontWeight: 500,
                        '&:hover': {
                          backgroundColor: theme.palette.purple[500],
                        },
                      },
                      'button.Mui-selected': {
                        backgroundColor: theme.palette.brand.primary,
                        color: '#fff',
                        fontWeight: 600,
                      },
                      '.MuiTabs-indicator': { display: 'none' },
                    }}
                  >
                    {tabs.map((tab, i) => (
                      <Tab key={i} label={tab?.Title} />
                    ))}
                  </Tabs>
                </Box>
              )}
            </Box>
          )}

          {/* Tab Panels */}
          {tabs.map((tab, i) => (
            <Box
              key={i}
              sx={{
                color:
                  props?.ComponentBackgroundColour === Componentbackgroundcolour.Primary
                    ? theme.palette.common.white
                    : 'inherit',
                display: i === value ? 'flex' : 'none',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: { xs: 3, md: 6 },
                py: { xs: 4, sm: 5 },
                px: { xs: 3, md: 5 },
                background: innerBackground,
                borderRadius: '2rem',
                mx: 'auto',
                maxWidth: '1200px',
              }}
            >
              <TwoColumnTextImageContent
                block={
                  {
                    Image: tab?.Image,
                    ImageAlignment: props?.ProductFeaturesImageAlignment,
                    ImageColRight: tab?.ImageColRight,
                    ImageColumnSize: props.ProductFeaturesImageColumnSize,
                    TwoColumnBody: tab?.Body,
                    TextColumnSize: props.ProductFeaturesTextColumnSize,
                    ImageBorder: props.ProductFeaturesImageBorder,
                    RoundImageCorners: props.ProductFeaturesRoundImageCorners,
                    ItemSpacing: props.ProductFeaturesItemSpacing,
                  } as ComponentBlocksTwoColumnTextImageBlock
                }
              />
            </Box>
          ))}
        </ClientOnly>
      </Box>
    </CustomBackground>
  );
}
